import React, { useState } from "react";
import { Film, Loader2, Play, CheckCircle2 } from "lucide-react";
import { fmtDuration } from "@/lib/api";
import { PhaseList, StepError } from "./Stepper";

const Stat = ({ label, value, id }) => (
  <div className="studio-card p-4"><div className="metric-label">{label}</div><div className="font-head text-2xl font-bold" data-testid={id}>{value}</div></div>
);

export const StepImport = ({ w, onNext }) => {
  const m = w.media || {};
  const done = w.steps.import.status === "done";
  return (
    <div className="space-y-4" data-testid="wizard-panel-import">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Stat label="Clips" value={m.total || 0} id="wizard-clips-total" />
        <Stat label="Ready to edit" value={`${m.ready || 0}`} id="wizard-clips-ready" />
        <Stat label="Quick-analysed" value={m.analyzed || 0} id="wizard-clips-analyzed" />
        <Stat label="Footage" value={fmtDuration(m.duration || 0)} id="wizard-footage-duration" />
      </div>
      {w.ingest_pending > 0 && <div className="metric-label">{w.ingest_pending} import job(s) still running…</div>}
      {w.failed_imports > 0 && <div className="metric-label text-[#fbbf24]">{w.failed_imports} file(s) failed to import. You can retry them from the Job Queue; the edit can go ahead without them.</div>}
      <div className="flex justify-end">
        <button className="btn btn-gold" disabled={!m.ready} onClick={onNext} data-testid="btn-wizard-to-rough">
          {done ? <CheckCircle2 size={15} /> : <Loader2 size={15} className="animate-spin" />} Next: Quick Rough Cut</button>
      </div>
    </div>
  );
};

export const StepRoughCut = ({ w, onStart, onRetry, onNext }) => {
  const st = w.steps.rough_cut;
  const [mins, setMins] = useState(w.target_minutes || 20);
  const [busy, setBusy] = useState(false);
  const p = w.pending_by_type || {};
  const analysisLeft = (p.ANALYZE_QUALITY || 0) + (p.DETECT_SCENES || 0) + (p.ANALYZE_AUDIO || 0) + (p.FINGERPRINT || 0);
  const extra = {
    analysis: analysisLeft ? `${analysisLeft} job(s) left` : null,
    tag: p.TAG_CLIP ? `${p.TAG_CLIP} of ${w.candidates || "?"} clips left` : null,
    render: w.render_job?.status === "running" ? `${w.render_job.progress || 0}%` : null,
  };
  const clamp = (v) => Math.min(240, Math.max(3, Math.round(+v) || 20));
  const go = async () => { const m = clamp(mins); setMins(m); setBusy(true); try { await onStart(m); } finally { setBusy(false); } };
  return (
    <div className="space-y-5" data-testid="wizard-panel-rough_cut">
      <p className="text-[#94A3B8] text-sm max-w-3xl">Only the best clips get AI-tagged. A story and edit plan are written from them, and a fast 540p preview renders from the proxies. You can watch each scene as soon as it renders (Step 3).</p>
      {st.status !== "running" && (
        <div className="flex items-end gap-3 flex-wrap">
          <div>
            <label className="metric-label block mb-1">Film length (minutes)</label>
            <input type="number" min="3" max="240" value={mins} onChange={(e) => setMins(e.target.value)} onBlur={() => setMins(clamp(mins))}
              className="studio-input !py-1.5 w-32" data-testid="wizard-target-minutes" />
          </div>
          <div className="flex gap-1.5 flex-wrap pb-0.5" data-testid="wizard-length-presets">
            {[["Teaser", 3], ["Highlight", 8], ["Short Film", 20], ["Full Film", w.suggested_minutes || 20]].map(([l, v]) => (
              <button key={l} type="button" onClick={() => setMins(v)} data-testid={`wizard-preset-${l.toLowerCase().replace(" ", "-")}`}
                className={`btn !py-1 text-xs ${+mins === v ? "btn-gold" : "btn-ghost"}`}>{l} · {v}m</button>
            ))}
          </div>
          <div className="metric-label pb-2" data-testid="wizard-length-suggestion">3–240 min · {w.usable_minutes ?? 0} min usable footage → suggested {w.suggested_minutes || 20} min</div>
          <button className="btn btn-gold" onClick={go} disabled={busy} data-testid="btn-wizard-start-rough">
            {busy ? <Loader2 size={15} className="animate-spin" /> : <Play size={15} />}{st.status === "done" ? "Rebuild rough cut" : "Build rough cut"}</button>
        </div>
      )}
      {st.status !== "pending" && <PhaseList phases={w.phases.rough_cut} step={st} eta={w.eta_seconds} extra={extra} />}
      <StepError step={st} onRetry={onRetry} />
      {(st.status === "done" || st.phases_done?.includes("plan")) && (
        <div className="flex justify-end">
          <button className="btn btn-gold" onClick={onNext} data-testid="btn-wizard-to-review"><Film size={15} />{st.status === "done" ? "Next: Review" : "Watch scenes as they render"}</button>
        </div>
      )}
    </div>
  );
};
